import * as React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '~/client/components/ui/avatar'
import { Button } from '~/client/components/ui/button'
import { Card } from '~/client/components/ui/card'
import { Sheet, SheetContent } from '~/client/components/ui/sheet'
import { useAuthStore, useEffectiveUserDetail } from '~/client/common/stores/auth'
import Avvvatars from 'avvvatars-react'
import { Crown, LogOut, X } from 'lucide-react'
import { BasilAddSolidIcon } from '~/client/components/icons'

type ProfileSheetProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onCreateEvent?: () => void
  onMyEvents?: () => void
  onMySharedLists?: () => void
  onSubscribe?: () => void
  onChangePassword?: () => void
}

export function ProfileSheet({ open, onOpenChange, onCreateEvent, onMyEvents, onMySharedLists, onSubscribe, onChangePassword }: ProfileSheetProps) {
  const logout = useAuthStore((s) => s.logout)
  // useEffectiveUserDetail: profile sheet shows the "viewed as" identity during impersonation.
  const userDetail = useEffectiveUserDetail()

  const displayName = userDetail ? `${[userDetail.first_name, userDetail.last_name].filter(Boolean).join(' ')}`.trim() || userDetail.email || 'User' : 'User'

  const organizationName = userDetail?.active_entity?.name || ''

  function handleLogout() {
    onOpenChange?.(false)
    logout()
  }

  function handleClose() {
    onOpenChange?.(false)
  }

  function handleCreateEvent() {
    onOpenChange?.(false)
    onCreateEvent?.()
  }

  function handleMyEvents() {
    onOpenChange?.(false)
    onMyEvents?.()
  }

  function handleMySharedLists() {
    onOpenChange?.(false)
    onMySharedLists?.()
  }

  function handleChangePassword() {
    onOpenChange?.(false)
    onChangePassword?.()
  }

  function handleSubscribe() {
    onOpenChange?.(false)
    onSubscribe?.()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" size="full" className="p-0" showClose={false}>
        <div className="bg-white h-full flex flex-col">
          {/* Header */}
          <div className="border-b border-[#e4e1e9] px-4 py-4">
            <div className="flex items-center justify-between">
              <p className="font-['Inter:Medium',_sans-serif] font-medium text-[#6a6262] text-[16px]">Menu</p>
              <button onClick={handleClose} className="flex items-center justify-center size-8 rounded-full hover:bg-gray-100 transition-colors">
                <X className="size-5 text-[#6a6262]" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            <div className="flex flex-col gap-6 p-4">
              {/* User Profile Section */}
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 ring-[3px] ring-[#8a3dff]">
                  <AvatarImage src={undefined} alt="profile" />
                  <AvatarFallback>
                    <Avvvatars size={40} style="character" value={userDetail?.email || ''} />
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium leading-[1.4]">
                  <p className="text-[#55534e] text-[16px]">{displayName}</p>
                  <p className="text-[#b6aec1] text-[14px]">{organizationName}</p>
                </div>
              </div>

              {/* Menu Card */}
              <Card className="border border-[#e4e1e9] rounded-[12px] overflow-hidden">
                {/* Subscribe to Plan */}
                <button
                  onClick={handleSubscribe}
                  className="w-full flex items-center justify-between p-4 bg-[rgba(249,197,101,0.04)] border-b border-[#e4e1e9] hover:bg-[rgba(249,197,101,0.08)] transition-colors"
                >
                  <span className="font-['Inter:Medium',_sans-serif] font-medium text-[#55534e] text-[14px]">Subscribe to Plan</span>
                  <Crown className="size-6 text-[#f9c565]" fill="#f9c565" />
                </button>

                {/* My Events */}
                <button onClick={handleMyEvents} className="w-full flex items-center justify-between p-4 border-b border-[#e4e1e9] hover:bg-gray-50 transition-colors">
                  <span className="font-['Inter:Medium',_sans-serif] font-medium text-[#55534e] text-[14px]">My Events</span>
                </button>

                {/* Shared Lists */}
                <button onClick={handleMySharedLists} className="w-full flex items-center justify-between p-4 border-b border-[#e4e1e9] hover:bg-gray-50 transition-colors">
                  <span className="font-['Inter:Medium',_sans-serif] font-medium text-[#55534e] text-[14px]">Shared Lists</span>
                </button>

                {/* Change Password */}
                <button onClick={handleChangePassword} className="w-full flex items-center justify-between p-4 border-b border-[#e4e1e9] hover:bg-gray-50 transition-colors">
                  <span className="font-['Inter:Medium',_sans-serif] font-medium text-[#55534e] text-[14px]">Change Password</span>
                </button>
              </Card>

              {/* Create Event Button */}
              <Button variant="revel" className="w-full h-[44px] rounded-[12px] gap-2" onClick={handleCreateEvent}>
                <span className="font-['Inter:Semi_Bold',_sans-serif] font-semibold text-[14px]">Create Event</span>
                <BasilAddSolidIcon className="size-6" />
              </Button>
            </div>

            {/* Logout Button at Bottom */}
            <div className="mt-auto border-t border-[#e4e1e9] px-4 py-4">
              <button onClick={handleLogout} className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded-md transition-colors">
                <LogOut className="size-6 text-[#d04b46]" />
                <span className="font-['Inter:Medium',_sans-serif] font-medium text-[#d04b46] text-[16px]">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default ProfileSheet
